import React from 'react'
import { useTranslation } from 'react-i18next'
import CamilaLogo from './CamilaLogo'

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear()
  const { t } = useTranslation()

  const quickLinks = [
    { id: 'home', label: t('nav.home') },
    { id: 'services', label: t('nav.services') },
    { id: 'cases', label: t('nav.cases') },
    { id: 'projects', label: t('nav.projects') },
    { id: 'contact', label: t('nav.contact') }
  ]

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    }
  }

  return (
    <footer className="border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* 品牌信息 */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center space-x-2 text-xl font-normal text-gray-900 mb-4">
              <CamilaLogo size={28} className="text-gray-900" />
              <span>Camila</span>
            </div>
            <p className="text-gray-600 mb-4 max-w-md font-light">
              {t('footer.brand.description')}
            </p>
            <div className="flex space-x-4">
              <a
                href="https://github.com/cychenhaibin"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 md:p-1.5 bg-gray-100 rounded-lg hover:bg-gray-200 text-gray-700 hover:text-[#4285F4] transition-all duration-200"
                title="GitHub"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
              </a>
              <a
                href="https://www.yuque.com/cychenhaibin"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 md:p-1.5 bg-gray-100 rounded-lg hover:bg-gray-200 text-gray-700 hover:text-[#4285F4] transition-all duration-200"
                title="语雀"
              >
                <div className="w-5 h-5">
                  {/* <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"> */}
                  <img src='https://mdn.alipayobjects.com/huamei_0prmtq/afts/img/A*PXAJTYXseTsAAAAAAAAAAAAADvuFAQ/original' alt="语雀" />
                  {/* </svg> */}
                </div>
              </a>
              <a
                href="mailto:haibinchenleo@outlook.com"
                className="p-2 md:p-1.5 bg-gray-100 rounded-lg hover:bg-gray-200 text-gray-700 hover:text-[#4285F4] transition-all duration-200"
                title="Email"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </a>
            </div>
          </div>

          {/* 快速链接 */}
          <div>
            <h3 className="text-lg font-normal text-gray-900 mb-4">{t('footer.quickLinks')}</h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="text-gray-600 hover:text-[#4285F4] transition-colors font-light"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* 联系我们 */}
          <div>
            <h3 className="text-lg font-normal text-gray-900 mb-4">{t('footer.contactMe')}</h3>
            <ul className="space-y-2 text-gray-600 font-light">
              <li>haibinchenleo@outlook.com</li>
              <li>+86 156 9688 6898</li>
              <li>cychenhaibin.github.io/website</li>
              <li>{t('contact.locationValue')}</li>
            </ul>
          </div>
        </div>

        {/* 版权信息 */}
        <div className="border-t border-gray-200 mt-8 pt-8 text-center">
          <p className="text-gray-600 font-light">
            © {currentYear} Camila. {t('footer.copyright')}
            <span className="mx-2">|</span>
            <span className="text-[#4285F4]">{t('footer.madeWith')}</span>
          </p>
        </div>
      </div>
    </footer>
  )
}

export default Footer
